/**
 * Sugar Town Commerce — reusable Commerce API layer.
 *
 * This module is the single source of truth for reading the native catalog
 * and managing carts. It is intentionally framework-agnostic (plain async
 * functions over the Supabase client) so the same layer can later power a
 * mobile app, Tallivo POS, wholesale portals, or marketplace vendors
 * without rewriting business logic. UI components and stores should call
 * THIS module rather than querying Supabase directly.
 */
import { supabase } from "@/integrations/supabase/client";
import type {
  CommerceBrand,
  CommerceCategory,
  CommerceCollection,
  CommerceProduct,
  CommerceProductFull,
  CommerceCartLine,
} from "./types";

const CART_TOKEN_KEY = "sugar-town-commerce-cart-token";

export function getCartToken(): string {
  let token = localStorage.getItem(CART_TOKEN_KEY);
  if (!token) {
    token = crypto.randomUUID();
    localStorage.setItem(CART_TOKEN_KEY, token);
  }
  return token;
}

// ---------- Catalog reads ----------

export async function getBrands(): Promise<CommerceBrand[]> {
  const { data, error } = await supabase
    .from("commerce_brands")
    .select("*")
    .eq("is_active", true)
    .order("sort_order", { ascending: true })
    .order("name", { ascending: true });
  if (error) throw error;
  return data ?? [];
}

export async function getCategories(): Promise<CommerceCategory[]> {
  const { data, error } = await supabase
    .from("commerce_categories")
    .select("*")
    .eq("is_active", true)
    .order("sort_order", { ascending: true });
  if (error) throw error;
  return data ?? [];
}

export async function getCollections(featuredOnly = false): Promise<CommerceCollection[]> {
  let query = supabase
    .from("commerce_collections")
    .select("*")
    .eq("is_active", true)
    .order("sort_order", { ascending: true });
  if (featuredOnly) query = query.eq("is_featured", true);
  const { data, error } = await query;
  if (error) throw error;
  return data ?? [];
}

export interface ProductFilter {
  brandSlug?: string;
  categorySlug?: string;
  collectionSlug?: string;
  search?: string;
  limit?: number;
}

export async function getProducts(filter: ProductFilter = {}): Promise<CommerceProduct[]> {
  let brandId: string | undefined;
  let categoryId: string | undefined;

  if (filter.brandSlug) {
    const { data } = await supabase.from("commerce_brands").select("id").eq("slug", filter.brandSlug).maybeSingle();
    if (!data) return [];
    brandId = data.id;
  }
  if (filter.categorySlug) {
    const { data } = await supabase.from("commerce_categories").select("id").eq("slug", filter.categorySlug).maybeSingle();
    if (!data) return [];
    categoryId = data.id;
  }

  // Collection filtering uses the join table.
  if (filter.collectionSlug) {
    const { data: col } = await supabase
      .from("commerce_collections")
      .select("id")
      .eq("slug", filter.collectionSlug)
      .maybeSingle();
    if (!col) return [];
    const { data: links } = await supabase
      .from("commerce_product_collections")
      .select("product_id")
      .eq("collection_id", col.id);
    const ids = (links ?? []).map((l) => l.product_id);
    if (ids.length === 0) return [];
    let q = supabase.from("commerce_products").select("*").in("id", ids).eq("status", "published");
    if (filter.limit) q = q.limit(filter.limit);
    const { data, error } = await q;
    if (error) throw error;
    return data ?? [];
  }

  let query = supabase.from("commerce_products").select("*").eq("status", "published");
  if (brandId) query = query.eq("brand_id", brandId);
  if (categoryId) query = query.eq("category_id", categoryId);
  if (filter.search) query = query.ilike("title", `%${filter.search}%`);
  if (filter.limit) query = query.limit(filter.limit);
  query = query.order("created_at", { ascending: false });

  const { data, error } = await query;
  if (error) throw error;
  return data ?? [];
}

export async function getProductBySlug(slug: string): Promise<CommerceProductFull | null> {
  const { data: product, error } = await supabase
    .from("commerce_products")
    .select("*")
    .eq("slug", slug)
    .maybeSingle();
  if (error) throw error;
  if (!product) return null;

  const [{ data: variants }, { data: images }, { data: brand }] = await Promise.all([
    supabase
      .from("commerce_product_variants")
      // NOTE: cost_price / wholesale_price are intentionally excluded — they
      // are sensitive margin data and SELECT is revoked for storefront roles.
      .select(
        "id, product_id, title, sku, barcode, options, price, sale_price, weight_grams, length_cm, width_cm, height_cm, inventory_quantity, inventory_policy, is_active, sort_order, created_at, updated_at"
      )
      .eq("product_id", product.id)
      .eq("is_active", true)
      .order("sort_order", { ascending: true }),
    supabase
      .from("commerce_product_images")
      .select("*")
      .eq("product_id", product.id)
      .order("sort_order", { ascending: true }),
    product.brand_id
      ? supabase.from("commerce_brands").select("id, slug, name").eq("id", product.brand_id).maybeSingle()
      : Promise.resolve({ data: null }),
  ]);

  const safeVariants = (variants ?? []).map((v) => ({
    ...v,
    cost_price: null,
    wholesale_price: null,
  }));

  return {
    ...product,
    variants: safeVariants,
    images: images ?? [],
    brand: brand ?? null,
  };
}


// ---------- Cart ----------

/**
 * Merge the local guest cart (identified by the browser's session token) into
 * the signed-in user's active cart. Call this right after a user signs in so
 * items added as a guest are not lost. Returns the resulting user cart id, or
 * null when called while signed out.
 */
export async function mergeGuestCart(): Promise<string | null> {
  const token = localStorage.getItem(CART_TOKEN_KEY);
  if (!token) return null;
  const { data, error } = await supabase.rpc("commerce_cart_merge", { p_token: token });
  if (error) throw error;
  return (data as string | null) ?? null;
}


/**
 * Resolve (or create) the active cart for the current visitor.
 *
 * Guest carts are protected by a secret session token that lives only in this
 * browser's localStorage. All cart access goes through SECURITY DEFINER RPCs
 * that verify ownership (signed-in user OR knowledge of the token), so one
 * visitor can never read or mutate another visitor's cart.
 */
export async function getOrCreateCart(): Promise<string> {
  const token = getCartToken();
  const { data, error } = await supabase.rpc("commerce_cart_resolve", { p_token: token });
  if (error) throw error;
  return data as string;
}

export async function getCartLines(cartId: string): Promise<CommerceCartLine[]> {
  const token = getCartToken();
  const { data, error } = await supabase.rpc("commerce_cart_lines", {
    p_cart_id: cartId,
    p_token: token,
  });
  if (error) throw error;
  return ((data as unknown) ?? []) as CommerceCartLine[];
}

export async function addToCart(opts: {
  cartId: string;
  productId: string;
  variantId: string;
  unitPrice: number;
  quantity?: number;
}): Promise<void> {
  const token = getCartToken();
  const { error } = await supabase.rpc("commerce_cart_add", {
    p_cart_id: opts.cartId,
    p_token: token,
    p_product_id: opts.productId,
    p_variant_id: opts.variantId,
    p_quantity: opts.quantity ?? 1,
    p_unit_price: opts.unitPrice,
  });
  if (error) throw error;
}

export async function updateCartItem(cartId: string, itemId: string, quantity: number): Promise<void> {
  const token = getCartToken();
  const { error } = await supabase.rpc("commerce_cart_set_qty", {
    p_cart_id: cartId,
    p_token: token,
    p_item_id: itemId,
    p_quantity: quantity,
  });
  if (error) throw error;
}

export async function removeCartItem(cartId: string, itemId: string): Promise<void> {
  const token = getCartToken();
  const { error } = await supabase.rpc("commerce_cart_remove", {
    p_cart_id: cartId,
    p_token: token,
    p_item_id: itemId,
  });
  if (error) throw error;
}


export function cartTotals(lines: CommerceCartLine[]): { count: number; subtotal: number } {
  return lines.reduce(
    (acc, l) => ({
      count: acc.count + l.quantity,
      subtotal: acc.subtotal + Number(l.unit_price) * l.quantity,
    }),
    { count: 0, subtotal: 0 }
  );
}

// ---------- Editorial commerce reads (Phase 3) ----------

/** Fetch a set of published products by id, preserving the requested order. */
export async function getProductsByIds(ids: string[]): Promise<CommerceProduct[]> {
  const unique = Array.from(new Set(ids.filter(Boolean)));
  if (unique.length === 0) return [];
  const { data, error } = await supabase
    .from("commerce_products")
    .select("*")
    .in("id", unique)
    .eq("status", "published");
  if (error) throw error;
  const byId = new Map((data ?? []).map((p) => [p.id, p]));
  return unique.map((id) => byId.get(id)).filter((p): p is CommerceProduct => Boolean(p));
}

/**
 * Story-driven cart recommendations. Suggests companion products based on the
 * shared wellness goals, concerns, ritual stages, brands and curated
 * "pair with" links of items already in the cart. Excludes products already
 * in the cart. Returns a small, ranked set.
 */
export async function getCartRecommendations(
  productIdsInCart: string[],
  limit = 4
): Promise<CommerceProduct[]> {
  const inCart = new Set(productIdsInCart);
  if (inCart.size === 0) return [];

  const cartProducts = await getProductsByIds(productIdsInCart);

  // 1. Honour explicit "pair with" curation first.
  const pairIds = cartProducts.flatMap((p) => (p.pair_with_product_ids as string[] | null) ?? []);
  const curated = (await getProductsByIds(pairIds)).filter((p) => !inCart.has(p.id));

  if (curated.length >= limit) return curated.slice(0, limit);

  // 2. Fall back to facet-based matching across the catalog.
  const goals = new Set(cartProducts.flatMap((p) => (p.wellness_goals as string[] | null) ?? []));
  const concerns = new Set(cartProducts.flatMap((p) => (p.concerns as string[] | null) ?? []));
  const stages = new Set(cartProducts.flatMap((p) => [p.ritual_stage].filter(Boolean) as string[]));

  const { data } = await supabase
    .from("commerce_products")
    .select("*")
    .eq("status", "published")
    .limit(60);

  const pool = (data ?? []).filter(
    (p) => !inCart.has(p.id) && !curated.some((c) => c.id === p.id)
  );

  const scored = pool
    .map((p) => {
      let score = 0;
      ((p.wellness_goals as string[] | null) ?? []).forEach((g) => goals.has(g) && (score += 2));
      ((p.concerns as string[] | null) ?? []).forEach((c) => concerns.has(c) && (score += 2));
      if (p.ritual_stage && stages.has(p.ritual_stage)) score += 1;
      return { p, score };
    })
    .filter((x) => x.score > 0)
    .sort((a, b) => b.score - a.score)
    .map((x) => x.p);

  return [...curated, ...scored].slice(0, limit);
}
